// Deux joysticks tactiles multitouch : gauche = déplacement, droit = visée/tir.
// Le droit tire au-delà de sa zone morte ; son amplitude donne la distance visée (armes à point).
import { getSettings } from './settings';

interface Stick {
  pointerId: number | null;
  baseX: number;
  baseY: number;
  x: number;
  y: number;
  base: HTMLDivElement;
  knob: HTMLDivElement;
}

export interface TouchSample {
  active: boolean;
  mx: number;
  my: number;
  aiming: boolean;
  aimAngle: number;
  /** Amplitude 0..1 après sensibilité. */
  aimAmp: number;
  fire: boolean;
}

const RADIUS = 62;

export class TouchControls {
  readonly root: HTMLDivElement;
  private left: Stick;
  private right: Stick;
  private lastAimAngle = 0;
  private lastAimAmp = 0.6;
  used = false;
  autofire = false;
  private disposers: (() => void)[] = [];

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'touch-layer';
    parent.appendChild(this.root);
    this.left = this.makeStick('left');
    this.right = this.makeStick('right');
    this.layoutFixed();
    // Sur écran tactile, les joysticks sont visibles dès le départ pour montrer où poser les pouces.
    if (matchMedia('(pointer: coarse)').matches) document.body.classList.add('touch-used');

    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      this.used = true;
      document.body.classList.add('touch-used');
      const s = e.clientX < window.innerWidth / 2 ? this.left : this.right;
      if (s.pointerId !== null) return;
      s.pointerId = e.pointerId;
      this.root.setPointerCapture?.(e.pointerId);
      if (!getSettings().joyFixed) {
        s.baseX = e.clientX;
        s.baseY = e.clientY;
      }
      s.x = e.clientX;
      s.y = e.clientY;
      this.render(s);
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      const s = e.pointerId === this.left.pointerId ? this.left : e.pointerId === this.right.pointerId ? this.right : null;
      if (!s) return;
      s.x = e.clientX;
      s.y = e.clientY;
      this.render(s);
      e.preventDefault();
    };
    const up = (e: PointerEvent) => {
      const s = e.pointerId === this.left.pointerId ? this.left : e.pointerId === this.right.pointerId ? this.right : null;
      if (!s) return;
      s.pointerId = null;
      s.x = s.baseX;
      s.y = s.baseY;
      if (!getSettings().joyFixed) this.layoutFixed(s);
      this.render(s);
    };
    this.root.addEventListener('pointerdown', down, { passive: false });
    this.root.addEventListener('pointermove', move, { passive: false });
    this.root.addEventListener('pointerup', up);
    this.root.addEventListener('pointercancel', up);
    const resize = () => this.layoutFixed();
    window.addEventListener('resize', resize);
    // Empêche le défilement et le zoom involontaires pendant le jeu.
    const block = (e: TouchEvent) => e.preventDefault();
    this.root.addEventListener('touchstart', block, { passive: false });
    this.root.addEventListener('touchmove', block, { passive: false });
    this.disposers.push(
      () => window.removeEventListener('resize', resize),
      () => this.root.remove(),
    );
  }

  private makeStick(side: 'left' | 'right'): Stick {
    const base = document.createElement('div');
    base.className = `joy-base joy-${side}`;
    const knob = document.createElement('div');
    knob.className = 'joy-knob';
    base.appendChild(knob);
    this.root.appendChild(base);
    return { pointerId: null, baseX: 0, baseY: 0, x: 0, y: 0, base, knob };
  }

  private layoutFixed(only?: Stick): void {
    const h = window.innerHeight;
    const w = window.innerWidth;
    const m = Math.max(90, Math.min(140, h * 0.22));
    const pos: [Stick, number, number][] = [
      [this.left, m + 10, h - m],
      [this.right, w - m - 10, h - m],
    ];
    for (const [s, x, y] of pos) {
      if (only && s !== only) continue;
      if (s.pointerId !== null) continue;
      s.baseX = x;
      s.baseY = y;
      s.x = x;
      s.y = y;
      this.render(s);
    }
  }

  private render(s: Stick): void {
    const op = getSettings().joyOpacity;
    s.base.style.opacity = String(s.pointerId !== null ? Math.min(1, op + 0.25) : op);
    s.base.style.transform = `translate(${s.baseX - RADIUS}px, ${s.baseY - RADIUS}px)`;
    let dx = s.x - s.baseX;
    let dy = s.y - s.baseY;
    const l = Math.hypot(dx, dy);
    if (l > RADIUS) {
      dx = (dx / l) * RADIUS;
      dy = (dy / l) * RADIUS;
    }
    s.knob.style.transform = `translate(${dx}px, ${dy}px)`;
  }

  private vec(s: Stick): { x: number; y: number; amp: number } {
    if (s.pointerId === null) return { x: 0, y: 0, amp: 0 };
    const dx = s.x - s.baseX;
    const dy = s.y - s.baseY;
    const l = Math.hypot(dx, dy);
    const amp = Math.min(1, l / RADIUS);
    return { x: l > 0 ? dx / l : 0, y: l > 0 ? dy / l : 0, amp };
  }

  sample(): TouchSample {
    const { deadzone, joySensitivity } = getSettings();
    const L = this.vec(this.left);
    const R = this.vec(this.right);
    let mx = 0;
    let my = 0;
    if (L.amp > deadzone) {
      const a = Math.min(1, (L.amp - deadzone) / (1 - deadzone));
      mx = L.x * a;
      my = L.y * a;
    }
    const aiming = R.amp > deadzone;
    if (aiming) {
      this.lastAimAngle = Math.atan2(R.y, R.x);
      this.lastAimAmp = Math.min(1, ((R.amp - deadzone) / (1 - deadzone)) * joySensitivity);
    }
    return {
      active: this.used,
      mx,
      my,
      aiming,
      aimAngle: this.lastAimAngle,
      aimAmp: this.lastAimAmp,
      // Le tir auto conserve le dernier point relatif direction/distance validé.
      fire: aiming || this.autofire,
    };
  }

  dispose(): void {
    for (const d of this.disposers) d();
  }
}
